import React, { useState, useRef } from 'react';

// --- SVG ICONS (Matching Form Modal) ---
const CloseIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

const MaximizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3H5a2 2 0 00-2 2v3m18 0V5a2 2 0 00-2-2h-3m0 18h3a2 2 0 002-2v-3M3 16v3a2 2 0 002 2h3"></path>
  </svg>
);

const MinimizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3v3a2 2 0 01-2 2H3m18 0h-3a2 2 0 01-2-2V3m0 18v-3a2 2 0 012-2h3M3 16h3a2 2 0 012 2v3"></path>
  </svg>
);

const PrintIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 6 2 18 2 18 9"></polyline>
    <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
    <rect x="6" y="14" width="12" height="8"></rect>
  </svg>
);

const API_URL = '/api';

const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'numeric',
    year: 'numeric'
  });
};

const SiteIndentViewModal = ({ open, onClose, data }) => {
  
  const [isMaximized, setIsMaximized] = useState(false);

  // --- RESIZABLE STATE & REFS ---
  const [columnWidths, setColumnWidths] = useState({
    lineNo: 80,
    productCode: 180,
    feederId: 140,
    taskNo: 100,
    description: 280,
    availStock: 220,
    pendingDPR: 220,
    qty: 220,
    activity: 180,      // <--- ADD THIS
    subActivity: 180,
    qtyIssued: 180,
    qtyReceived: 180,
    uom: 80,
    remark:280
  });

  const resizingRef = useRef(null);

  // Defensive parsing: Handle both { header, lines } and flattened objects
  const header = data?.header || data || {};
  const lines = data?.lines || data?.Lines || [];

  // Helper to format currency or numbers
  const formatNumber = (num) => {
    return num ? parseFloat(num).toFixed(6) : '0.00';
  };

  const toggleMaximize = () => {
    setIsMaximized(!isMaximized);
  };

  // --- RESIZABLE HANDLERS ---
  const startResize = (columnKey, startX) => {
    resizingRef.current = {
      columnKey,
      startX,
      startWidth: columnWidths[columnKey]
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', stopResize);
  };

  const handleMouseMove = (e) => {
    if (!resizingRef.current) return;

    const { columnKey, startX, startWidth } = resizingRef.current;
    const delta = e.clientX - startX;

    setColumnWidths(prev => ({
      ...prev,
      [columnKey]: Math.max(60, startWidth + delta)
    }));
  };

  const stopResize = () => {
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', stopResize);
    resizingRef.current = null;
  };

  // --- RESIZABLE TH COMPONENT ---
  const ResizableTh = ({ width, onResize, children, align = 'left' }) => (
    <th
      style={{ width }}
      className={`relative p-2 ${align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : ''} select-none`}
    >
      {children}
      <div
        className="absolute top-0 right-0 h-full w-1 cursor-col-resize bg-transparent hover:bg-blue-400"
        onMouseDown={(e) => onResize(e.clientX)}
      />
    </th>
  );

  if (!open) return null;

  return (
    <>
      <div className={`fixed inset-0 z-50 flex justify-center transition-all duration-300 ease-in-out ${open ? 'opacity-100 visible' : 'opacity-0 invisible'}`}>
        <style>
          {`
            .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
            .hide-scrollbar::-webkit-scrollbar { display: none; }
            .custom-scrollbar::-webkit-scrollbar { height: 8px; }
            .custom-scrollbar::-webkit-scrollbar-track { background: #f1f5f9; border-radius: 10px; }
            .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
            .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
            
            .resizable-table {
              table-layout: fixed;
              min-width: max-content;
            }
          `}
        </style>
        <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
        
        <div className={`bg-white shadow-2xl w-full flex flex-col transition-transform transform-gpu duration-300 ease-in-out ${
          open ? 'translate-x-0' : 'translate-x-full'
        } ${ isMaximized ? 'h-full max-w-full' : 'h-full max-w-6xl' }`} tabIndex={0}>
          
          {/* --- MODAL HEADER (Exact Copy of Form Design) --- */}
          <div className="flex-shrink-0 p-8 pb-6">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold text-[#384959]">View Site Indent</h2>
                <p className="text-sm text-[#64748b] mt-1 font-medium">
                  No: <span className="text-[#384959]">{header.SiteIndentNo || 'Draft'}</span>
                  {header.Status && (
                    <span className={`ml-3 px-2 py-0.5 rounded text-xs font-bold uppercase border ${
                      header.Status === 'Open' ? 'bg-green-100 text-green-700 border-green-200' :
                      header.Status === 'Released' ? 'bg-blue-100 text-blue-700 border-blue-200' :
                      'bg-gray-100 text-gray-700 border-gray-200'
                    }`}>
                      {header.Status}
                    </span>
                  )}
                </p>
              </div>
              
              <div className="flex items-center gap-2">
                <button 
                  className="text-[#A0AEC0] hover:text-[#384959] transition-colors p-1" 
                  title="Print"
                  onClick={() => window.print()}
                >
                  <PrintIcon size={20} />
                </button>
                <button className="text-[#A0AEC0] hover:text-[#384959] transition-colors p-1" onClick={toggleMaximize}>
                  {isMaximized ? <MinimizeIcon size={20} /> : <MaximizeIcon size={20} />}
                </button>
                <button className="text-[#A0AEC0] hover:text-[#384959] transition-colors p-1" onClick={onClose}>
                  <CloseIcon size={24} />
                </button>
              </div>
            </div>
          </div>

          {/* --- FORM BODY --- */}
          <div className="flex-grow overflow-y-auto hide-scrollbar px-8">
            <div className="space-y-6 pb-6">
              
              {/* --- HEADER SECTION (Read Only) --- */}
              <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
                <h3 className="text-lg font-semibold text-[#384959] mb-4">Site Indent Information</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
                  
                  {/* 1. TYPE */}
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Type</label>
                    <div className="w-full bg-white border border-[#E2E8F0] rounded-lg px-3 py-2 text-[#384959] shadow-sm">
                      {header.TransferType || '-'}
                    </div>
                  </div>

                  {/* 2. DATE */}
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Date</label>
                    <div className="w-full bg-white border border-[#E2E8F0] rounded-lg px-3 py-2 text-[#384959] shadow-sm">
                      {formatDate(header.Date)}
                    </div>
                  </div>

                  {/* 3. VENDOR */}
                <div className="flex flex-col gap-2">
                  <label className="block text-sm font-medium text-[#64748b]">Constructor / Vendor</label>
                  <div className="w-full bg-white border border-[#E2E8F0] rounded-lg px-3 py-2 text-[#384959] shadow-sm">
                    {header.VendorCode || '-'} - {header.VendorName || '-'}
                  </div>
                </div>

                  {/* 4. PROJECT */}
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Project</label>
                    <div className="w-full bg-white border border-[#E2E8F0] rounded-lg px-3 py-2 text-[#384959] shadow-sm">
                      {header.ProjectNo || '-'} - {header.ProjectDescription || '-'}
                    </div>
                  </div>

                  {/* 5. TO FEEDER */}
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">To Feeder (Target)</label>
                    <div className="w-full bg-white border border-[#E2E8F0] rounded-lg px-3 py-2 text-[#384959] shadow-sm">
                      {header.ToFeederCode } - {header.ToFeederDescription}
                    </div>
                  </div>

                  {/* 6. BOQ SELECTION */}
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Survey BOQ Ref</label>
                    <div className="w-full bg-white border border-[#E2E8F0] rounded-lg px-3 py-2 text-[#384959] shadow-sm">
                      {header.SurveyBOQNo || '-'}
                    </div>
                  </div>

                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Activity</label>
                    <div className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50 text-gray-700">
                      {header.ActivityCode } - {header.ActivityDesc} 
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Sub Activity</label>
                    <div className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50 text-gray-700">
                      {header.SubActivityCode } - {header.SubActivityDesc} 
                    </div>
                  </div>

                  {/* 7. STATUS */}
                                  {/* 7. STATUS - EXISTING */}
                {/* <div className="flex flex-col gap-2">
                  <label className="block text-sm font-medium text-[#64748b]">Status</label>
                  <div className="w-full bg-white border border-[#E2E8F0] rounded-lg px-3 py-2 text-[#384959] shadow-sm">
                    {header.Status || '-'}
                  </div>
                </div> */}

               {/* 8. APPROVAL/BC STATUS */}
              <div className="flex flex-col gap-2">
                <label className="block text-sm font-medium text-[#64748b]"> Status</label>
                <div className="w-full bg-white border border-[#E2E8F0] rounded-lg px-3 py-2 shadow-sm">
                  {header.ApprovalStatus === 'PENDING' ? (
                    <span className="text-xs font-medium text-amber-700 bg-amber-50 px-2 py-1 rounded border border-amber-200">
                      Pending Approval (Level {header.CurrentLevel || 1})
                    </span>
                  ) : header.ApprovalStatus === 'REJECTED' ? (
                    <span className="text-xs font-semibold text-red-600 bg-red-50 px-2 py-1 rounded border border-red-200">
                      Rejected
                    </span>
                  ) : header.ApprovalStatus === 'APPROVED' ? (
                    // FIX: Check for both SendToBC and SendToBc
                    (header.SendToBC === 1 || header.SendToBC === true || header.SendToBc === 1 || header.SendToBc === true) ? (
                      <span className="text-xs font-semibold text-green-600 bg-green-50 px-2 py-1 rounded border border-green-200">
                        Sent to BC Successfully
                      </span>
                    ) : (
                      <span className="text-xs font-medium text-blue-700 bg-blue-50 px-2 py-1 rounded border border-blue-200">
                        Approved (Ready to Send BC)
                      </span>
                    )
                  ) : (
                    <span className="text-xs text-gray-500 italic">
                      {(header.SendToBC === 1 || header.SendToBC === true || header.SendToBc === 1 || header.SendToBc === true) ? 'Sent to BC Successfully' : 'Open'}
                    </span>
                  )}
                </div>
              </div>

                  {/* 8. FROM FEEDER */}
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">From Feeder</label>
                    <div className="w-full bg-white border border-[#E2E8F0] rounded-lg px-3 py-2 text-[#384959] shadow-sm">
                      {header.FromFeederCode} - {header.FromFeederDescription}
                    </div>
                  </div>

                  {/* AUTOFILLED FIELDS */}
                  
                  {/* 9. CIRCLE */}
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Circle</label>
                    <div className="w-full bg-white border border-[#E2E8F0] rounded-lg px-3 py-2 text-[#384959] shadow-sm uppercase">
                      {header.Circle || '-'}
                    </div>
                  </div>

                  {/* 10. DISTRICT */}
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">District</label>
                    <div className="w-full bg-white border border-[#E2E8F0] rounded-lg px-3 py-2 text-[#384959] shadow-sm uppercase">
                      {header.District || '-'}
                    </div>
                  </div>

                  {/* 11. DIVISION */}
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Division</label>
                    <div className="w-full bg-white border border-[#E2E8F0] rounded-lg px-3 py-2 text-[#384959] shadow-sm uppercase">
                      {header.Division || '-'}
                    </div>
                  </div>

                  {/* 12. SUBDIVISION */}
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Sub Division</label>
                    <div className="w-full bg-white border border-[#E2E8F0] rounded-lg px-3 py-2 text-[#384959] shadow-sm uppercase">
                      {header.SubDivision || '-'}
                    </div>
                  </div>
                  
                  {/* 13. SUB STATION */}
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Sub Station</label>
                    <div className="w-full bg-white border border-[#E2E8F0] rounded-lg px-3 py-2 text-[#384959] shadow-sm uppercase">
                      {header.SubStation || '-'}
                    </div>
                  </div>

                  {/* 14. REMARKS */}
                  <div className="flex flex-col gap-2 md:col-span-2 lg:col-span-2">
                    <label className="block text-sm font-medium text-[#64748b]">Remarks</label>
                    <div className="w-full bg-white border border-[#E2E8F0] rounded-lg px-3 py-2 text-[#384959] shadow-sm min-h-[60px]">
                      {header.Remarks || '-'}
                    </div>
                  </div>
                </div>
              </div>

              {/* --- LINES SECTION (Read Only Table) --- */}
              <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-[#384959]">Indent Lines</h3>
                  <div className="text-sm font-medium text-[#64748b]">
                    Count: {lines.length}
                  </div>
                </div>
                
                <div className="overflow-x-auto custom-scrollbar">
                  <table className="resizable-table border border-[#E2E8F0]">
                    <thead>
                      <tr className="bg-[#BDDDFC] text-[#384959]">
                        <ResizableTh width={columnWidths.lineNo} onResize={(x) => startResize('lineNo', x)} align="center">
                          Line No
                        </ResizableTh>
                        <ResizableTh width={columnWidths.productCode} onResize={(x) => startResize('productCode', x)}>
                          Product Code
                        </ResizableTh>
                        <ResizableTh width={columnWidths.feederId} onResize={(x) => startResize('feederId', x)}>
                          Feeder ID
                        </ResizableTh>
                        <ResizableTh width={columnWidths.taskNo} onResize={(x) => startResize('taskNo', x)}>
                          Task No
                        </ResizableTh>
                        <ResizableTh width={columnWidths.description} onResize={(x) => startResize('description', x)}>
                          Description
                        </ResizableTh> 
                         <ResizableTh width={columnWidths.uom} onResize={(x) => startResize('uom', x)} align="center">
                          UOM
                        </ResizableTh>
                         <ResizableTh width={columnWidths.activity} onResize={(x) => startResize('activity', x)}>
                          Activity
                        </ResizableTh>
                        <ResizableTh width={columnWidths.subActivity} onResize={(x) => startResize('subActivity', x)}>
                          Sub Activity
                        </ResizableTh>
                        <ResizableTh width={columnWidths.availStock} onResize={(x) => startResize('availStock', x)} align="right">
                          Avail Stock
                        </ResizableTh>
                         <ResizableTh width={columnWidths.pendingDPR} onResize={(x) => startResize('pendingDPR', x)} align="right">
                          Pending DPR Qty
                        </ResizableTh>
                        <ResizableTh width={columnWidths.qty} onResize={(x) => startResize('qty', x)} align="right">
                          Qty
                        </ResizableTh>
                        <ResizableTh width={columnWidths.qtyIssued} onResize={(x) => startResize('qtyIssued', x)} align="right">
                          Qty Issued
                        </ResizableTh>
                        <ResizableTh width={columnWidths.qtyReceived} onResize={(x) => startResize('qtyReceived', x)} align="right">
                          Qty Received
                        </ResizableTh>
                        <ResizableTh width={columnWidths.qtyReceived} onResize={(x) => startResize('remark', x)} align="right">
                          Remark
                        </ResizableTh>
                       
                      </tr>
                    </thead>
                    <tbody>
                      {lines.length === 0 ? (
                        <tr>
                          <td colSpan="11" className="p-8 text-center text-[#94A3B8]">
                            No lines found.
                          </td>
                        </tr>
                      ) : (
                        lines.map((line, idx) => (
                          <tr key={idx} className="border-t border-[#E2E8F0] hover:bg-[#F1F5F9]">
                            <td className="p-2 text-center text-[#384959]" style={{ width: columnWidths.lineNo }}>
                              {line.LineNumber || idx + 1}
                            </td>
                            <td className="p-2 font-medium text-[#384959] uppercase" style={{ width: columnWidths.productCode }}>
                              {line.ProductCode || '-'}
                            </td>
                            <td className="p-2 text-[#64748b]" style={{ width: columnWidths.feederId }}>
                              {line.FeederCode} -{line.FeederDescription}
                            </td>
                            <td className="p-2 text-[#64748b]" style={{ width: columnWidths.taskNo }}>
                              {line.TaskNo || '-'}
                            </td>
                            <td className="p-2 text-[#384959]" style={{ width: columnWidths.description }}>
                              {line.ProductName || '-'}
                            </td>
                            <td className="p-2 text-center text-[#64748b] uppercase" style={{ width: columnWidths.uom }}>
                              {line.UnitOfMeasure || '-'}
                            </td>
                             <td className="p-2 text-[#384959]" style={{ width: columnWidths.activity }}>
                              {line.ActivityCode ? `${line.ActivityCode} - ${line.ActivityDescription}` : '-'}
                            </td>
                            <td className="p-2 text-[#384959]" style={{ width: columnWidths.subActivity }}>
                              {line.SubActivityCode ? `${line.SubActivityCode} - ${line.SubActivityDescription}` : '-'}
                            </td>
                            <td className="p-2 text-right text-[#64748b]" style={{ width: columnWidths.availStock }}>
                              {formatNumber(line.AvailableStock)}
                            </td>
                            <td className="p-2 text-right font-medium text-[#384959]" style={{ width: columnWidths.pendingDPR }}>
                              {formatNumber(line.PendingDPRQty)}
                            </td>
                            <td className="p-2 text-right font-medium text-[#384959]" style={{ width: columnWidths.qty }}>
                              {formatNumber(line.QtyToIssue)}
                            </td>
                            <td className="p-2 text-right font-bold text-green-600" style={{ width: columnWidths.qtyIssued }}>
                                {formatNumber(line.TotalIssued)}
                            </td>
                            <td className="p-2 text-right font-bold text-green-600" style={{ width: columnWidths.qtyReceived }}>
                              {formatNumber(line.TotalReceived)}
                            </td>
                            <td className="p-2 text-right  text-[#384959]" style={{ width: columnWidths.remark }}>
                              {line.Remark}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                
                {/* Summary Footer */}
                <div className="mt-6 bg-white p-4 rounded-lg border border-[#E2E8F0]">
                  <h4 className="text-md font-semibold text-[#384959] mb-4">Total Summary</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="flex flex-col">
                      <span className="text-sm text-gray-500">Total Lines</span>
                      <span className="text-base font-semibold">{lines.length}</span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-sm text-gray-500">Total Quantity</span>
                      <span className="text-base font-semibold text-lg text-[#384959]">
                        {formatNumber(lines.reduce((acc, line) => acc + (parseFloat(line.Quantity) || 0), 0))}
                      </span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-sm text-gray-500">Total To Issue</span>
                      <span className="text-base font-semibold text-lg text-blue-600">
                        {formatNumber(lines.reduce((acc, line) => acc + (parseFloat(line.QtyToIssue) || 0), 0))}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* --- FOOTER --- */}
          <div className="flex-shrink-0 p-8 pt-0">
            <div className="flex justify-end gap-3">
              <button 
                type="button" 
                className="px-6 py-3 rounded-lg text-[#64748b] font-semibold transition-all duration-300 ease-in-out hover:bg-[#F1F5F9]" 
                onClick={onClose}
              >
                Close
              </button>
            </div>
          </div>

        </div>
      </div>
    </>
  );
};

export default SiteIndentViewModal;